import { ICellRendererParams } from 'ag-grid-community';
import clsx from 'clsx';
import { z } from 'zod';

import { GoogleFontIcon } from '../../icons/GoogleFontIcon';
import { SVGIcon } from '../../icons/SVGIcon';
import { Tooltip } from '../../tooltip/Tooltip';

import { DATA_ATTRIBUTE_PREVENT_AG_GRID_ON_ROW_CLICKED } from './constants';

import styles from './iconCellRenderer.module.scss';

const IconCellRendererValue = z.object({
  disabled: z.boolean().optional(),
  name: z.string(),
  svgIcon: z.boolean().optional(),
  tooltip: z.string().optional(),
});

type IconCellRendererValue = z.infer<typeof IconCellRendererValue>;

function isIconCellRendererValue(
  value: unknown
): value is IconCellRendererValue {
  return IconCellRendererValue.safeParse(value).success;
}

export type IIconCellRendererParams = ICellRendererParams<
  unknown,
  IconCellRendererValue
>;

export const IconCellRenderer = ({ value }: IIconCellRendererParams) => {
  if (value === undefined) {
    return;
  }

  if (!isIconCellRendererValue(value)) {
    throw new Error(
      `IconCellRenderer called with wrong value format: ${JSON.stringify(value, null, 2)}`
    );
  }

  const { disabled, name, svgIcon, tooltip } = value;

  return (
    <div
      {...DATA_ATTRIBUTE_PREVENT_AG_GRID_ON_ROW_CLICKED}
      data-testid="icon-renderer-cell"
      className={styles['root']}
    >
      <Tooltip label={tooltip} disabled={tooltip == null}>
        {svgIcon ? (
          <span>
            <SVGIcon
              name={name}
              className={clsx(
                styles['icon'],
                disabled && styles['icon--disabled']
              )}
            />
          </span>
        ) : (
          <GoogleFontIcon
            name={name}
            solid
            className={clsx(
              styles['icon'],
              disabled && styles['icon--disabled']
            )}
          />
        )}
      </Tooltip>
    </div>
  );
};
